import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotification } from "../notification.hook";
import {
  COURSES_PAGE_SIZE,
  RefreshReactQuery,
} from "../../constants/AppConstants";
import {
  DATA_UPDATED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../../constants/Dictionary";
import { UpdateCollaboratorsCmSheetCourseRequest } from "../../types/api/collaborators.type";
import { useEffect } from "react";
import CmSheetCoursesService from "../../services/api/cmSheet/CmSheetCourses.service";
import CmSheetAvailableCoursesService from "../../services/api/cmSheet/CmSheetAvailableCourses.service";

export const CM_SHEET_COURSES_QUERY_KEY = "get-cm-sheet-courses";
export const CM_SHEET_AVAILABLE_COURSES_QUERY_KEY =
  "get-cm-sheet-available-courses";

export const useCmSheetCourses = (cmSheetId: number) => {
  const { notification } = useNotification();
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: [CM_SHEET_COURSES_QUERY_KEY, cmSheetId],
    queryFn: () => CmSheetCoursesService.list(cmSheetId),
    staleTime: RefreshReactQuery.MODERATE,
  });
  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useUpdateCmSheetCourses = (cmSheetId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isUpdating } = useMutation({
    mutationFn: ({
      body,
    }: {
      body: UpdateCollaboratorsCmSheetCourseRequest;
      successMessage?: string;
    }) => CmSheetCoursesService.update(cmSheetId, body),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [CM_SHEET_COURSES_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [CM_SHEET_AVAILABLE_COURSES_QUERY_KEY],
      });
      const message = variables.successMessage ?? DATA_UPDATED_SUCCESS_LABEL;
      notification.success(message, undefined, "toast");
    },
    onError: (error) => {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  const updateCmSheetCourses = (
    body: UpdateCollaboratorsCmSheetCourseRequest,
    successMessage?: string,
  ) => mutateAsync({ body, successMessage });

  return { updateCmSheetCourses, isUpdating };
};

interface UseCmSheetAvailableCoursesProps {
  cmSheetId: number;
  pageNumber: number;
  selectedCategories: string[];
  searchDebouncedValue: string;
}

export const useCmSheetAvailableCourses = ({
  cmSheetId,
  pageNumber,
  selectedCategories,
  searchDebouncedValue,
}: UseCmSheetAvailableCoursesProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: [
      CM_SHEET_AVAILABLE_COURSES_QUERY_KEY,
      cmSheetId,
      pageNumber,
      selectedCategories,
      searchDebouncedValue,
    ],
    queryFn: ({ signal }) =>
      CmSheetAvailableCoursesService.list(
        cmSheetId,
        COURSES_PAGE_SIZE,
        pageNumber,
        [
          ...(selectedCategories
            ? [{ key: "category", value: selectedCategories }]
            : []),
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};
